//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useEffect, useRef, useState } from 'react';
import { FieldArrayWithId } from 'react-hook-form';
import styled from 'styled-components';

import { VariantRow } from '@campaigns/components/shared/product-details-modal/drawer/variants/VariantRow';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';

interface VariantsTableProps {
  fields: FieldArrayWithId<EditableBVSamplingProduct, 'variations'>[];
  isViewOnly: boolean;
  onRemoveVariant: (index: number) => void;
}

export const VariantsTable = ({
  fields,
  isViewOnly,
  onRemoveVariant,
}: VariantsTableProps) => {
  const [expandedFieldIds, setExpandedFieldIds] = useState<Set<string>>(
    () => new Set(fields[0] ? [fields[0].id] : []),
  );
  const hasSeededExpansionRef = useRef(fields.length > 0);

  useEffect(() => {
    // Mounting empty (e.g. no variants yet) skips the initializer above, so seed once
    // the first field appears, without overwriting expansion state on later renders
    const firstField = fields[0];
    if (!hasSeededExpansionRef.current && firstField) {
      hasSeededExpansionRef.current = true;
      setExpandedFieldIds((previous) =>
        previous.size === 0 ? new Set([firstField.id]) : previous,
      );
    }
  }, [fields]);

  const toggleExpansion = (fieldId: string) => {
    setExpandedFieldIds((previous) => {
      const next = new Set(previous);
      if (next.has(fieldId)) {
        next.delete(fieldId);
      } else {
        next.add(fieldId);
      }
      return next;
    });
  };

  const handleRemove = (index: number) => {
    const removedFieldId = fields[index]?.id;
    onRemoveVariant(index);
    setExpandedFieldIds((previous) => {
      const next = new Set(previous);
      if (removedFieldId) {
        next.delete(removedFieldId);
      }
      // Promote a surviving row so removal never leaves the table fully collapsed
      if (next.size === 0) {
        const remaining = fields.filter((_, i) => i !== index);
        if (remaining[0]) {
          next.add(remaining[0].id);
        }
      }
      return next;
    });
  };

  return (
    <Container>
      {fields.map((field, index) => (
        <VariantRow
          index={index}
          isExpanded={expandedFieldIds.has(field.id)}
          isViewOnly={isViewOnly}
          key={field.id}
          onRemove={() => handleRemove(index)}
          onToggleExpansion={() => toggleExpansion(field.id)}
          totalVariations={fields.length}
        />
      ))}
    </Container>
  );
};

const Container = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.gray2};
  display: flex;
  flex-direction: column;
`;
